import React, { useMemo, useEffect } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Print } from "@mui/icons-material";
import CustomDataGrid from '../../../../components/ui/customDataGrid/customDataGrid.component';
import { CLIENT_CONTRACT_TERMS_ENDPOINT } from '../../../../consts';
import { useHasPermission } from '../../../../contexts/auth';
import { useBreadcrumbs } from '../../../../components/ui/breadcrumps/breadcrumbsContext';
import GridAddressCell from '../../../../components/ui/gridAddressCell/gridAddressCell.component';
import EntityReferenceLink from '../../../../components/ui/entityReference/entityReferenceLink.component';

export const ClientEquipmentsActive = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { setOverride, clearOverride } = useBreadcrumbs();
  const canViewEquipments = useHasPermission("settlement.view_equipment");
  const canViewContracts = useHasPermission("settlement.view_contract");

  useEffect(() => {
    setOverride(location.pathname, t('Active equipment'));
    return () => {
      clearOverride(location.pathname);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "id",
      headerName: "ID",
    },
    {
      field: "equipment__name",
      headerName: t("Name"),
      valueGetter: (value, row) => row.equipment?.name,
      renderCell: (params) => (
        <EntityReferenceLink
          entityType="equipment"
          entityId={params.row.equipment?.id}
          label={params.row.equipment?.name || "-"}
          canPreview={canViewEquipments}
        />
      ),
    },
    {
      field: "equipment__serial",
      headerName: t("Serial Number"),
      valueGetter: (value, row) => row.equipment?.serial,
    },
    {
      field: "installation_address",
      headerName: t("Installation Address"),
      sortable: false,
      renderCell: (params) => {
        return <GridAddressCell address={params.row.equipment?.installation_address} />;
      },
      valueGetter: (value, row) => {
        const address = row.equipment?.installation_address;
        return `${address?.street}, ${address?.city}, ${address?.zip_code}, ${address?.country}`;
      },
    },
    {
      field: "contract__number",
      headerName: t("Contract Number"),
      valueGetter: (value, row) => row.contract?.number,
      renderCell: (params) => (
        <EntityReferenceLink
          entityType="contract"
          entityId={params.row.contract?.id}
          label={params.row.contract?.number || "-"}
          canPreview={canViewContracts}
        />
      ),
    },
    {
      field: "contract__start_date",
      headerName: t("Contract Start Date"),
      valueGetter: (value, row) => row.contract?.start_date,
    },
    {
      field: "contract__end_date",
      headerName: t("Contract End Date"),
      valueGetter: (value, row) => row.contract?.end_date,
    },
  ], [t, canViewEquipments, canViewContracts]);

  const handleRowClick = (equipmentId: string) => {
    navigate(`/equipment-section/equipments/${equipmentId}`);
  };

  if (!canViewEquipments) {
    return null;
  }

  const today = new Date().toISOString().split('T')[0];
  const activeEquipmentsUrl = `${CLIENT_CONTRACT_TERMS_ENDPOINT}?client_id=${id}&end_date_gte=${today}`;

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <CustomDataGrid
        icon={<Print />}
        title={t('Active equipment')}
        url={activeEquipmentsUrl}
        gridSettingsKey="CLIENT_ACTIVE_EQUIPMENTS"
        columns={columns}
        onRowClick={handleRowClick}
      />
    </Box>
  );
};
